import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Users, Wallet, Store, Mic, Plus, RefreshCw, TrendingUp,
  DollarSign, Receipt, PiggyBank, X, StickyNote, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, formatMoney } from "@/lib/eventData";

const STATUSES = ["New Lead", "Contacted", "Planning", "Vendor Research", "Quote Sent", "Booked", "Completed", "Cancelled"];
const STATUS_COLOR = {
  "New Lead": "bg-nick-primary text-white",
  "Contacted": "bg-amber-500 text-white",
  "Planning": "bg-nick-secondary text-white",
  "Vendor Research": "bg-indigo-500 text-white",
  "Quote Sent": "bg-purple-500 text-white",
  "Booked": "bg-emerald-600 text-white",
  "Completed": "bg-teal-600 text-white",
  "Cancelled": "bg-slate-400 text-white",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, v, h] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/events"),
        api.get("/admin/vendors"),
        api.get("/admin/hosts"),
      ]);
      setStats(s.data);
      setEvents(e.data);
      setVendors(v.data);
      setHosts(h.data);
    } catch {
      toast.error("Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const shown = filter ? events.filter((e) => e.status === filter) : events;

  return (
    <div className="min-h-screen bg-nick-bg">
      <header className="sticky top-0 z-40 nick-glass border-b border-black/5">
        <div className="mx-auto max-w-7xl px-5 h-16 flex items-center justify-between">
          <div className="font-display font-extrabold text-lg tracking-tight">
            Let's Party <span className="text-nick-primary">Houston</span>
            <span className="ml-2 text-xs font-semibold text-nick-muted">Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" data-testid="refresh-btn" onClick={load} className="rounded-full">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <span className="hidden sm:inline text-sm text-nick-muted">{user?.email}</span>
            <Button variant="outline" size="sm" data-testid="logout-btn" onClick={doLogout} className="rounded-full font-semibold">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Leads" value={stats?.total_events ?? "—"} tone="primary" />
          <StatCard icon={DollarSign} label="Total Revenue" value={formatMoney(stats?.total_revenue)} tone="emerald" />
          <StatCard icon={Receipt} label="Est. Expenses" value={formatMoney(stats?.estimated_expenses)} tone="amber" />
          <StatCard icon={PiggyBank} label="Est. Profit" value={formatMoney(stats?.estimated_profit)} tone="secondary" />
        </div>

        <Tabs defaultValue="pipeline" className="mt-8">
          <TabsList className="rounded-full bg-white border border-nick-border p-1 h-auto flex-wrap">
            <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="rounded-full data-[state=active]:bg-nick-primary data-[state=active]:text-white px-4 py-2 font-semibold">
              <Users className="h-4 w-4 mr-1.5" /> Leads
            </TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue" className="rounded-full data-[state=active]:bg-nick-primary data-[state=active]:text-white px-4 py-2 font-semibold">
              <TrendingUp className="h-4 w-4 mr-1.5" /> Revenue
            </TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors" className="rounded-full data-[state=active]:bg-nick-primary data-[state=active]:text-white px-4 py-2 font-semibold">
              <Store className="h-4 w-4 mr-1.5" /> Vendors
            </TabsTrigger>
            <TabsTrigger value="hosts" data-testid="tab-hosts" className="rounded-full data-[state=active]:bg-nick-primary data-[state=active]:text-white px-4 py-2 font-semibold">
              <Mic className="h-4 w-4 mr-1.5" /> Hosts
            </TabsTrigger>
          </TabsList>

          {/* PIPELINE */}
          <TabsContent value="pipeline" className="mt-6">
            <div className="flex gap-2 overflow-x-auto nick-scroll pb-2">
              <FilterPill active={filter === ""} onClick={() => setFilter("")} label={`All (${events.length})`} />
              {STATUSES.map((s) => (
                <FilterPill key={s} active={filter === s} onClick={() => setFilter(s)}
                  label={`${s} (${stats?.by_status?.[s] ?? 0})`} />
              ))}
            </div>

            {loading ? (
              <Centered><Loader2 className="h-6 w-6 animate-spin text-nick-primary" /></Centered>
            ) : shown.length === 0 ? (
              <Centered>No leads yet. They'll show up here the moment a customer submits.</Centered>
            ) : (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shown.map((e) => (
                  <button key={e.id} data-testid={`event-card-${e.event_number}`} onClick={() => setSelected(e)}
                    className="text-left rounded-2xl bg-white border border-nick-border p-5 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-nick-muted">{e.event_number}</span>
                      <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${STATUS_COLOR[e.status] || "bg-slate-200"}`}>{e.status}</span>
                    </div>
                    <h3 className="mt-2 font-display font-bold text-lg">{e.first_name} {e.last_name}</h3>
                    <p className="text-sm text-nick-muted">{e.event_types?.join(", ") || "—"}</p>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-nick-muted">
                      <span>{e.guest_count || "—"} guests</span>
                      <span>{e.budget_range || "—"}</span>
                      <span>{e.zip_or_city || "—"}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>

          {/* REVENUE */}
          <TabsContent value="revenue" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <RevCard label="Customer Revenue" value={formatMoney(stats?.customer_revenue)} sub="Planning fees & coordination" />
              <RevCard label="Vendor Revenue" value={formatMoney(stats?.vendor_revenue)} sub="Referral & partner revenue" />
              <RevCard label="Total Revenue" value={formatMoney(stats?.total_revenue)} sub="All sources combined" highlight />
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <RevCard label="Estimated Expenses" value={formatMoney(stats?.estimated_expenses)} sub="Across all events" />
              <RevCard label="Estimated Profit" value={formatMoney(stats?.estimated_profit)} sub="Revenue minus expenses" />
            </div>
            <p className="mt-4 text-sm text-nick-muted">
              Revenue is calculated from the financials you enter on each event. Internal profit figures are never shown to customers.
            </p>
          </TabsContent>

          {/* VENDORS */}
          <TabsContent value="vendors" className="mt-6">
            <VendorManager vendors={vendors} onChange={load} />
          </TabsContent>

          {/* HOSTS */}
          <TabsContent value="hosts" className="mt-6">
            <HostManager hosts={hosts} onChange={load} />
          </TabsContent>
        </Tabs>
      </main>

      {selected && (
        <EventDialog event={selected} onClose={() => setSelected(null)} onChange={async () => { await load(); }}
          refresh={async (id) => { const { data } = await api.get(`/admin/events/${id}`); setSelected(data); }} />
      )}
    </div>
  );
}

function Centered({ children }) {
  return <div className="mt-10 flex items-center justify-center text-nick-muted text-center min-h-[120px]">{children}</div>;
}

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = {
    primary: "bg-nick-primary/10 text-nick-primary",
    emerald: "bg-emerald-500/10 text-emerald-600",
    amber: "bg-amber-500/10 text-amber-600",
    secondary: "bg-nick-secondary/10 text-nick-secondary",
  };
  return (
    <div className="rounded-2xl bg-white border border-nick-border p-5">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-3 font-display font-extrabold text-2xl">{value}</div>
      <div className="text-sm text-nick-muted">{label}</div>
    </div>
  );
}

function RevCard({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-2xl border p-6 ${highlight ? "bg-nick-dark text-white border-nick-dark" : "bg-white border-nick-border"}`}>
      <div className={`overline ${highlight ? "text-white/70" : "text-nick-primary"}`}>{label}</div>
      <div className="mt-2 font-display font-extrabold text-3xl">{value}</div>
      <div className={`mt-1 text-sm ${highlight ? "text-white/70" : "text-nick-muted"}`}>{sub}</div>
    </div>
  );
}

function FilterPill({ active, onClick, label }) {
  return (
    <button onClick={onClick} data-testid={`filter-${label}`}
      className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
        active ? "bg-nick-dark border-nick-dark text-white" : "bg-white border-nick-border text-nick-dark hover:border-nick-dark/40"
      }`}>
      {label}
    </button>
  );
}

function EventDialog({ event, onClose, onChange, refresh }) {
  const [note, setNote] = useState("");
  const [fin, setFin] = useState({
    nick_revenue: event.financials?.nick_revenue ?? "",
    vendor_referral_revenue: event.financials?.vendor_referral_revenue ?? "",
    vendor_costs: event.financials?.vendor_costs ?? "",
    customer_price: event.financials?.customer_price ?? "",
    expenses: event.financials?.expenses ?? "",
  });
  const [saving, setSaving] = useState(false);

  const setStatus = async (status) => {
    await api.patch(`/admin/events/${event.id}/status`, { status });
    toast.success(`Moved to ${status}`);
    await refresh(event.id);
    await onChange();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/events/${event.id}/notes`, { text: note });
    setNote("");
    toast.success("Note added");
    await refresh(event.id);
  };

  const saveFin = async () => {
    setSaving(true);
    const payload = {};
    Object.entries(fin).forEach(([k, v]) => { payload[k] = v === "" ? 0 : Number(v); });
    await api.patch(`/admin/events/${event.id}/financials`, payload);
    setSaving(false);
    toast.success("Financials saved");
    await refresh(event.id);
    await onChange();
  };

  const services = [
    ...Object.entries(event.services || {}).map(([c, items]) => `${c}: ${items.join(", ")}`),
    ...(event.nick_decide || []).map((c) => `${c}: Let Nick decide`),
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto nick-scroll rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between pr-6">
            <span>{event.first_name} {event.last_name}</span>
            <span className="font-mono text-xs text-nick-muted">{event.event_number}</span>
          </DialogTitle>
        </DialogHeader>

        {/* status */}
        <div>
          <Label className="font-semibold text-sm">Status</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button key={s} data-testid={`set-status-${s}`} onClick={() => setStatus(s)}
                className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${
                  event.status === s ? STATUS_COLOR[s] + " border-transparent" : "bg-white border-nick-border text-nick-dark hover:border-nick-dark/40"
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* details */}
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <Detail label="Email" value={event.email} />
          <Detail label="Phone" value={event.phone} />
          <Detail label="Preferred contact" value={event.preferred_contact || "—"} />
          <Detail label="Event type" value={event.event_types?.join(", ") || "—"} />
          <Detail label="Date" value={event.date_unknown ? "Unknown" : (event.event_date || "—")} />
          <Detail label="Time" value={`${event.start_time || "—"} - ${event.end_time || "—"}`} />
          <Detail label="Location" value={event.location_type || "—"} />
          <Detail label="Area" value={event.zip_or_city || "—"} />
          <Detail label="Guests" value={event.guest_count || "—"} />
          <Detail label="Budget" value={event.budget_range || "—"} />
          <Detail label="Package" value={event.planning_package ? `${event.planning_package}${event.planning_package_price != null ? ` (${formatMoney(event.planning_package_price)})` : ""}` : "Not selected"} />
          <Detail label="Picked tier" value={event.selected_tier || "—"} />
          <Detail label="Budget help" value={event.needs_budget_help ? "Yes" : "No"} />
        </div>

        {event.needs_budget_help && event.budget_help_notes && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            <strong>Budget help:</strong> {event.budget_help_notes}
          </div>
        )}

        <div>
          <Label className="font-semibold text-sm">Services requested</Label>
          <div className="mt-1 text-sm text-nick-dark space-y-1">
            {services.length ? services.map((s) => <div key={s}>{s}</div>) : <span className="text-nick-muted">None</span>}
          </div>
        </div>

        {event.notes && (
          <div>
            <Label className="font-semibold text-sm">Customer notes</Label>
            <p className="mt-1 text-sm text-nick-muted">{event.notes}</p>
          </div>
        )}

        {event.estimate?.has_estimate && (
          <div className="rounded-xl bg-nick-bg border border-nick-border p-3 text-sm">
            <span className="font-semibold">Estimate — </span>
            Budget {formatMoney(event.estimate.budget)} · Nick's Pick {formatMoney(event.estimate.recommended)} · Premium {formatMoney(event.estimate.premium)}
          </div>
        )}

        {/* financials */}
        <div className="rounded-xl border border-nick-border p-4">
          <Label className="font-semibold text-sm flex items-center gap-1.5"><Wallet className="h-4 w-4" /> Financials (internal)</Label>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <MoneyInput label="Customer price" k="customer_price" fin={fin} setFin={setFin} />
            <MoneyInput label="Vendor costs" k="vendor_costs" fin={fin} setFin={setFin} />
            <MoneyInput label="Nick revenue" k="nick_revenue" fin={fin} setFin={setFin} />
            <MoneyInput label="Vendor referral rev." k="vendor_referral_revenue" fin={fin} setFin={setFin} />
            <MoneyInput label="Expenses" k="expenses" fin={fin} setFin={setFin} />
          </div>
          <Button data-testid="save-financials" onClick={saveFin} disabled={saving} size="sm"
            className="mt-3 rounded-full bg-nick-dark hover:bg-black text-white font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save financials"}
          </Button>
        </div>

        {/* notes */}
        <div>
          <Label className="font-semibold text-sm flex items-center gap-1.5"><StickyNote className="h-4 w-4" /> Nick's notes</Label>
          <div className="mt-2 space-y-2">
            {(event.admin_notes || []).map((n) => (
              <div key={n.id} className="rounded-lg bg-nick-bg border border-nick-border p-2.5 text-sm">
                <p>{n.text}</p>
                <span className="text-xs text-nick-muted">{new Date(n.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <Input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note…" className="rounded-xl" />
            <Button data-testid="add-note" onClick={addNote} className="rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white">Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs text-nick-muted">{label}</div>
      <div className="font-medium break-words">{value}</div>
    </div>
  );
}

function MoneyInput({ label, k, fin, setFin }) {
  return (
    <div>
      <Label className="text-xs text-nick-muted">{label}</Label>
      <Input data-testid={`fin-${k}`} type="number" value={fin[k]}
        onChange={(e) => setFin((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 h-10 rounded-lg" />
    </div>
  );
}

const CATEGORY_KEYS = CATEGORIES.map((c) => c.key);
const EMPTY_VENDOR = {
  business_name: "", category: CATEGORY_KEYS[0], contact_person: "", phone: "", email: "", website: "",
  service_area: "", price_range: "", budget_price: "", recommended_price: "", premium_price: "",
  nick_rating: "", services: "", specialties: "", notes: "", partner_status: "", referral_fee: "",
  last_verified_date: "", active: true,
};

function VendorManager({ vendors, onChange }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(EMPTY_VENDOR);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!v.business_name.trim()) { toast.error("Business name required"); return; }
    setSaving(true);
    const payload = { ...v };
    ["budget_price", "recommended_price", "premium_price", "nick_rating", "referral_fee"].forEach((k) => {
      payload[k] = payload[k] === "" ? null : Number(payload[k]);
    });
    try {
      await api.post("/admin/vendors", payload);
      toast.success("Vendor added");
      setOpen(false); setV(EMPTY_VENDOR);
      await onChange();
    } catch { toast.error("Could not save vendor"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => { await api.delete(`/admin/vendors/${id}`); toast.success("Vendor removed"); await onChange(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-nick-muted">Vendors stay internal until you verify them. Pay-to-rank is never used.</p>
        <Button data-testid="add-vendor-btn" onClick={() => setOpen(true)} className="rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Add Vendor
        </Button>
      </div>

      {vendors.length === 0 ? (
        <Centered>No vendors yet. Add trusted businesses to recommend.</Centered>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((vd) => (
            <div key={vd.id} data-testid={`vendor-card-${vd.business_name}`} className="rounded-2xl bg-white border border-nick-border p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display font-bold">{vd.business_name}</h3>
                  <span className="text-xs font-semibold text-nick-primary">{vd.category}</span>
                </div>
                <button onClick={() => remove(vd.id)} data-testid={`delete-vendor-${vd.business_name}`} className="text-nick-muted hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
              <div className="mt-2 text-sm text-nick-muted space-y-0.5">
                {vd.contact_person && <div>{vd.contact_person}</div>}
                {vd.phone && <div>{vd.phone}</div>}
                {vd.service_area && <div>{vd.service_area}</div>}
                {vd.recommended_price != null && <div>Recommended: {formatMoney(vd.recommended_price)}</div>}
              </div>
              {!vd.active && <span className="mt-2 inline-block text-xs bg-slate-100 rounded-full px-2 py-0.5">Inactive</span>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto nick-scroll rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Add Vendor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <VField label="Business name *" k="business_name" v={v} setV={setV} span />
            <div className="col-span-2">
              <Label className="text-xs text-nick-muted">Category</Label>
              <select data-testid="vendor-category" value={v.category} onChange={(e) => setV({ ...v, category: e.target.value })}
                className="mt-1 w-full h-10 rounded-lg border border-nick-border bg-white px-3 text-sm">
                {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <VField label="Contact person" k="contact_person" v={v} setV={setV} />
            <VField label="Phone" k="phone" v={v} setV={setV} />
            <VField label="Email" k="email" v={v} setV={setV} />
            <VField label="Website" k="website" v={v} setV={setV} />
            <VField label="Service area" k="service_area" v={v} setV={setV} />
            <VField label="Price range" k="price_range" v={v} setV={setV} />
            <VField label="Budget price" k="budget_price" v={v} setV={setV} type="number" />
            <VField label="Recommended price" k="recommended_price" v={v} setV={setV} type="number" />
            <VField label="Premium price" k="premium_price" v={v} setV={setV} type="number" />
            <VField label="Nick's rating (1-5)" k="nick_rating" v={v} setV={setV} type="number" />
            <VField label="Referral fee" k="referral_fee" v={v} setV={setV} type="number" />
            <VField label="Partner status" k="partner_status" v={v} setV={setV} />
            <VField label="Specialties" k="specialties" v={v} setV={setV} span />
            <VField label="Services" k="services" v={v} setV={setV} span />
          </div>
          <Button data-testid="save-vendor" onClick={save} disabled={saving} className="rounded-full bg-nick-primary hover:bg-nick-primary-hover text-white font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Vendor"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VField({ label, k, v, setV, type = "text", span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <Label className="text-xs text-nick-muted">{label}</Label>
      <Input data-testid={`vendor-${k}`} type={type} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="mt-1 h-10 rounded-lg" />
    </div>
  );
}

const EMPTY_HOST = { name: "", bio: "", service_area: "", specialties: "", rate: "", availability: "", nick_approved: false, active: true };

function HostManager({ hosts, onChange }) {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState(EMPTY_HOST);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!h.name.trim()) { toast.error("Host name required"); return; }
    setSaving(true);
    const payload = { ...h, rate: h.rate === "" ? null : Number(h.rate) };
    try {
      await api.post("/admin/hosts", payload);
      toast.success("Host added");
      setOpen(false); setH(EMPTY_HOST);
      await onChange();
    } catch { toast.error("Could not save host"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="rounded-2xl bg-nick-secondary/5 border border-nick-secondary/20 p-5">
        <h3 className="font-display font-bold text-lg">Nick as your host</h3>
        <p className="mt-1 text-sm text-nick-muted">
          Up to 4 hours included with Nick's Full Service. Additional hosting $75/hour.
          This section is built for the future — Version 1 emphasizes Nick.
        </p>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-nick-muted">Future hosts (trained on the "Nick Standard")</p>
        <Button data-testid="add-host-btn" onClick={() => setOpen(true)} variant="outline" className="rounded-full font-semibold">
          <Plus className="h-4 w-4 mr-1" /> Add Host
        </Button>
      </div>

      {hosts.length === 0 ? (
        <Centered>No additional hosts yet.</Centered>
      ) : (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {hosts.map((hs) => (
            <div key={hs.id} className="rounded-2xl bg-white border border-nick-border p-5">
              <h3 className="font-display font-bold">{hs.name}</h3>
              {hs.rate != null && <div className="text-sm text-nick-muted">{formatMoney(hs.rate)}</div>}
              {hs.specialties && <p className="mt-1 text-sm text-nick-muted">{hs.specialties}</p>}
              {hs.nick_approved && <span className="mt-2 inline-block text-xs bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Nick-approved</span>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Add Host</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs text-nick-muted">Name *</Label>
              <Input data-testid="host-name" value={h.name} onChange={(e) => setH({ ...h, name: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
            <div><Label className="text-xs text-nick-muted">Rate</Label>
              <Input data-testid="host-rate" type="number" value={h.rate} onChange={(e) => setH({ ...h, rate: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
            <div><Label className="text-xs text-nick-muted">Service area</Label>
              <Input value={h.service_area} onChange={(e) => setH({ ...h, service_area: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
            <div className="col-span-2"><Label className="text-xs text-nick-muted">Specialties</Label>
              <Input value={h.specialties} onChange={(e) => setH({ ...h, specialties: e.target.value })} className="mt-1 h-10 rounded-lg" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={h.nick_approved} onChange={(e) => setH({ ...h, nick_approved: e.target.checked })} /> Nick-approved
            </label>
          </div>
          <Button data-testid="save-host" onClick={save} disabled={saving} className="rounded-full bg-nick-secondary hover:bg-nick-secondary/90 text-white font-bold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Host"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
